import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import { toast } from 'react-hot-toast';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import CalendarHeader from './CalendarHeader';
import CalendarWeekHeader from './CalendarWeekHeader';
import {
  CALENDAR_YEAR_SPAN_BACK,
  CALENDAR_YEAR_SPAN_FORWARD,
  generateWeeksForFiveYears,
  generateWeeksForInclusiveYearRange,
  CalendarWeekRow,
} from './CalendarMonthView';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import DayEventsDialog from './DayEventsDialog';
import {
  loadMultiCalendarFilterFromStorage,
  persistMultiCalendarFilter,
  coerceMultiCalendarFilter,
  multiCalendarFiltersEqual,
} from '../../moduleServices/planner_calendar_multi_filter';
import { plannerGoogleManualSync } from '../../moduleServices/planner_google_api';
import { PLANNER_TIME_FORMAT_24 } from '../../moduleServices/planner_time_format';
import { buildEventsByDateKeyIndex } from '../viewServices/calendar_grid_utils';
import { useDevice } from 'services/DeviceContext';

const MIN_WEEK_ROW_HEIGHT_PX = 76;

/** Sun–Sat chip row (~py-1 + ~26px) — subtract from react-window list height */
const WEEK_HEADER_HEIGHT_PX = 36;
const OVERSCAN_COUNT = 3;
/** Idle delay after last `onScroll` before snapping to whole week rows (ms). */
const WEEK_SNAP_IDLE_MS = 35;

/** Long smooth scroll feels bad beyond this gap (week-rows) — fake scroll + instant jump instead. */
const WEEK_ROW_JUMP_USE_FADE_THRESHOLD = 12;
const JUMP_OUT_MS = 130;
const JUMP_IN_MS = 160;
const JUMP_SLIDE_OUT_PX = 26;
const JUMP_SLIDE_IN_PX = 38;

/** Wash + muted grid lines — sem backdrop-blur (menos custo de composição no scroll). */
const CAL_GRID_WASH_CLASSES =
  'pointer-events-none absolute inset-0 z-0 bg-gradient-to-br from-white/[0.98] via-slate-50/[0.97] to-slate-100/[0.92]';
/** ~7 linhas verticais (colunas tipo dia); altura próxima a uma linha de semana. */
const CAL_GRID_LINES_CLASSES =
  'pointer-events-none absolute inset-0 z-0 opacity-[0.55] bg-[linear-gradient(to_right,rgb(148_163_184_/_0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgb(148_163_184_/_0.09)_1px,transparent_1px)] bg-[length:14.285714%_88px]';

/** react-window outer scroller: single styled rail (avoids “double” scrollbar look next to transitions). */
const CalendarListOuter = React.forwardRef(function CalendarListOuter(props, forwardedRef) {
  const { style, className, ...rest } = props;
  return (
    <div
      {...rest}
      ref={forwardedRef}
      style={{
        ...style,
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(148, 163, 184, 0.82) rgba(241, 245, 249, 0.75)',
      }}
      className={[
        className,
        '[&::-webkit-scrollbar]:w-[10px]',
        '[&::-webkit-scrollbar-track]:my-3 [&::-webkit-scrollbar-track]:mx-0.5 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-gradient-to-b [&::-webkit-scrollbar-track]:from-slate-100/95 [&::-webkit-scrollbar-track]:to-slate-200/65 [&::-webkit-scrollbar-track]:shadow-[inset_0_0_1px_rgb(226_232_240_/_0.9)]',
        '[&::-webkit-scrollbar-thumb]:min-h-[44px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-[3px] [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-slate-400/82 [&::-webkit-scrollbar-thumb]:bg-clip-padding',
        '[&::-webkit-scrollbar-thumb]:shadow-[inset_0_1px_0_rgb(255_255_255_/_0.5)] [&::-webkit-scrollbar-thumb]:hover:bg-slate-500/88',
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
});

CalendarListOuter.displayName = 'CalendarListOuter';

/**
 * Directional faux-scroll: drift + fade out → instant scroll → slide in + fade (no multi-second native smooth).
 * `fwd` +1 → target newer than viewport / scroll grows; −1 → older.
 */
function runDirectionalMonthJumpTransition(fadeWrap, outerEl, targetTop, reduceMotion, fwd) {
  if (!outerEl) return;

  const top = typeof targetTop === 'number' && Number.isFinite(targetTop) ? targetTop : 0;
  const dir = fwd < 0 ? -1 : 1;

  if (reduceMotion || !fadeWrap) {
    outerEl.scrollTo({ top: top, behavior: 'auto' });
    return;
  }

  const easeOut = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const easeIn = 'cubic-bezier(0.22, 1, 0.36, 1)';

  fadeWrap.style.willChange = 'opacity, transform';
  fadeWrap.style.transition = `opacity ${JUMP_OUT_MS}ms ${easeOut}, transform ${JUMP_OUT_MS}ms ${easeOut}`;
  fadeWrap.style.opacity = '0';
  fadeWrap.style.transform = `translate3d(0, ${-dir * JUMP_SLIDE_OUT_PX}px, 0)`;

  window.setTimeout(() => {
    outerEl.scrollTo({ top: top, behavior: 'auto' });

    fadeWrap.style.transition = 'none';
    fadeWrap.style.opacity = '0.12';
    fadeWrap.style.transform = `translate3d(0, ${dir * JUMP_SLIDE_IN_PX}px, 0)`;
    void fadeWrap.offsetWidth;

    fadeWrap.style.transition = `opacity ${JUMP_IN_MS}ms ${easeIn}, transform ${JUMP_IN_MS}ms ${easeIn}`;
    fadeWrap.style.opacity = '1';
    fadeWrap.style.transform = 'translate3d(0, 0, 0)';

    window.setTimeout(() => {
      fadeWrap.style.transition = '';
      fadeWrap.style.opacity = '';
      fadeWrap.style.transform = '';
      fadeWrap.style.removeProperty('will-change');
    }, JUMP_IN_MS + 70);
  }, JUMP_OUT_MS);
}

/** Full-page / overlay feedback while viewport is measured or virtual list scroll aligns. */
function CalendarViewportLoading({ title, subtitle, compact }) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 px-4 text-center ${
        compact ? 'py-4' : 'py-10'
      }`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <CalendarDaysIcon
        className={`shrink-0 text-blue-700/85 ${compact ? 'h-10 w-10' : 'h-14 w-14'}`}
        aria-hidden
      />
      <SpinnerLoad />
      <div className="space-y-0.5">
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle ? (
          <p className={`mx-auto text-gray-500 ${compact ? 'max-w-[16rem] text-xs' : 'max-w-[20rem] text-xs'}`}>
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ##### LocalStorage key for calendar filter
const STORAGE_KEY_CALENDAR_FILTER = 'planner_calendar_calendar_filter';

function getStoredCalendarFilter() {
  return loadMultiCalendarFilterFromStorage(STORAGE_KEY_CALENDAR_FILTER);
}

const getTodayKey = () => {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Visible row more than halfway → snap to current row index; otherwise next row */
function snapScrollTopToWeekRow(scrollTop, rowHeightPx, maxScroll) {
  const H = rowHeightPx;
  if (H <= 0 || maxScroll <= 0) return Math.max(0, Math.min(scrollTop, maxScroll));
  const bounded = Math.max(0, Math.min(scrollTop, maxScroll));
  const i = Math.floor(bounded / H);
  const r = bounded - i * H;
  const targetRaw = r < H / 2 ? i * H : (i + 1) * H;
  return Math.round(Math.max(0, Math.min(targetRaw, maxScroll)));
}

/** DOM fallback when ResizeObserver/`contentRect` is still zero (flex first paint). */
function readScrollContainerBoxHeight(el) {
  if (!el || typeof window === 'undefined') return 0;
  const r = el.getBoundingClientRect();
  const hRect = typeof r.height === 'number' && Number.isFinite(r.height) ? r.height : 0;
  const ch =
    typeof el.clientHeight === 'number' && Number.isFinite(el.clientHeight) ? el.clientHeight : 0;
  return Math.max(hRect, ch, 0);
}

/** Prefer `contentRect`; support `borderBoxSize`; fallback to synchronous box read */
function heightFromResizeObserverEntry(entry, el) {
  if (entry?.contentRect) {
    const h = entry.contentRect.height;
    if (typeof h === 'number' && Number.isFinite(h)) return Math.max(0, h);
  }
  const bbs = entry?.borderBoxSize;
  const firstBb = Array.isArray(bbs) ? bbs[0] : bbs;
  if (firstBb && typeof firstBb.blockSize === 'number' && Number.isFinite(firstBb.blockSize)) {
    return Math.max(0, firstBb.blockSize);
  }
  return readScrollContainerBoxHeight(el);
}

const PlannerCalendarView = ({
  events = [],
  isEventsLoading = false,
  calendars = [],
  weekRowsVisible = 6,
  /** Server list window `{ year_from, year_to, ... }` — aligns grid with filtered payload */
  eventsWindow = null,
  refreshEvents,
  onEventClick,
  onNewEventForDate,
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
}) => {
  const { isMobile } = useDevice();
  const [daySheet, setDaySheet] = useState(null);
  const rowsTarget = weekRowsVisible === 5 ? 5 : 6;
  const scrollContainerRef = useRef(null);
  const listRef = useRef(null);
  const listOuterRef = useRef(null);
  /** Inner wrapper around react-window List — opacity fade on big month jumps. */
  const calendarListFadeRef = useRef(null);
  const snapIdleTimerRef = useRef(null);
  const userInitiatedScrollToRef = useRef(false);
  /** Blocks month-from-scroll until first programmatic scrollToMonth (avoids top-of-list phantom Jan/year). */
  const initialScrollSyncedRef = useRef(false);
  const [listSnapPulse, setListSnapPulse] = useState(false);
  const [scrollContainerHeight, setScrollContainerHeight] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCalendarIds, setSelectedCalendarIdsState] = useState(getStoredCalendarFilter);
  const [eventSearch, setEventSearch] = useState('');
  const [googleSyncBusy, setGoogleSyncBusy] = useState(false);
  /** False until first scroll-to-month runs in layout (avoids flashing wrong weeks / double paint). */
  const [calendarGridReady, setCalendarGridReady] = useState(false);

  const clientYearFallback = new Date().getFullYear();

  const yWindowFrom = typeof eventsWindow?.year_from === 'number' ? eventsWindow.year_from : null;
  const yWindowTo = typeof eventsWindow?.year_to === 'number' ? eventsWindow.year_to : null;
  const hasServerWindow =
    yWindowFrom != null && yWindowTo != null;

  const minPickerYear = hasServerWindow
    ? yWindowFrom
    : clientYearFallback - CALENDAR_YEAR_SPAN_BACK;
  const maxPickerYear = hasServerWindow
    ? yWindowTo
    : clientYearFallback + CALENDAR_YEAR_SPAN_FORWARD;

  const weeks = useMemo(() => {
    if (hasServerWindow) {
      return generateWeeksForInclusiveYearRange(yWindowFrom, yWindowTo);
    }
    return generateWeeksForFiveYears(clientYearFallback);
  }, [hasServerWindow, yWindowFrom, yWindowTo, clientYearFallback]);
  const todayKey = useMemo(() => getTodayKey(), []);

  const listHeight = Math.max(0, scrollContainerHeight - WEEK_HEADER_HEIGHT_PX);

  /** Each row is listHeight split into `rowsTarget` bands (density from settings). */
  const rowHeightPx = useMemo(() => {
    if (listHeight <= 0) return MIN_WEEK_ROW_HEIGHT_PX;
    const fromViewport = Math.floor(listHeight / rowsTarget);
    return Math.max(MIN_WEEK_ROW_HEIGHT_PX, fromViewport);
  }, [listHeight, rowsTarget]);

  const clearSnapIdleTimer = useCallback(() => {
    if (snapIdleTimerRef.current != null) {
      clearTimeout(snapIdleTimerRef.current);
      snapIdleTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearSnapIdleTimer(), [clearSnapIdleTimer]);

  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;

    const applyHeight = (raw) => {
      if (!(typeof raw === 'number' && Number.isFinite(raw) && raw >= 0)) return;
      setScrollContainerHeight((prev) => (Math.abs(prev - raw) < 0.5 ? prev : raw));
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries?.[0];
      const h = entry ? heightFromResizeObserverEntry(entry, el) : readScrollContainerBoxHeight(el);
      applyHeight(h);
    });
    observer.observe(el);

    applyHeight(readScrollContainerBoxHeight(el));

    /** First tab open sometimes leaves flex height at 0 until the next frames; RO may not repeat. */
    const MAX_MEASURE_FRAMES = 48;
    let frames = 0;
    let rafId = 0;
    const remeasureUntilStable = () => {
      frames += 1;
      const h = readScrollContainerBoxHeight(el);
      applyHeight(h);
      if (h > WEEK_HEADER_HEIGHT_PX || frames >= MAX_MEASURE_FRAMES) return;
      rafId = window.requestAnimationFrame(remeasureUntilStable);
    };
    if (readScrollContainerBoxHeight(el) <= WEEK_HEADER_HEIGHT_PX) {
      rafId = window.requestAnimationFrame(remeasureUntilStable);
    }

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      applyHeight(readScrollContainerBoxHeight(el));
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (rafId) window.cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  const runWeekSnap = useCallback(() => {
    snapIdleTimerRef.current = null;
    const outer = listOuterRef.current;
    if (!outer || rowHeightPx <= 0) return;

    const maxScroll = Math.max(0, outer.scrollHeight - outer.clientHeight);
    if (maxScroll <= 0) return;

    const scrollTop = outer.scrollTop;
    const target = snapScrollTopToWeekRow(scrollTop, rowHeightPx, maxScroll);
    if (Math.abs(target - scrollTop) < 2) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

    setListSnapPulse(true);
    window.setTimeout(() => setListSnapPulse(false), 260);

    outer.scrollTo({ top: target, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [rowHeightPx]);

  /** Map month navigation to list scroll; `'smooth'` for picker/Today after grid is ready. */
  const scrollToMonth = useCallback(
    (monthDate, { behavior: preference = 'auto' } = {}) => {
      clearSnapIdleTimer();
      if (!weeks.length) return;
      const targetYear = monthDate.getFullYear();
      const targetMonth = monthDate.getMonth();
      const index = weeks.findIndex((week) =>
        week.some(
          (d) =>
            d.getFullYear() === targetYear && d.getMonth() === targetMonth && d.getDate() === 1
        )
      );
      if (index < 0) return;

      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
      const useSmooth = preference === 'smooth' && !reduceMotion;

      const outer = listOuterRef.current;
      const fadeWrap = calendarListFadeRef.current;
      if (outer && rowHeightPx > 0) {
        const maxScroll = Math.max(0, outer.scrollHeight - outer.clientHeight);
        const targetTop = Math.min(Math.max(0, index * rowHeightPx), maxScroll);
        const approxFrom = Math.round(outer.scrollTop / rowHeightPx);
        const fromIndex = Math.min(weeks.length - 1, Math.max(0, approxFrom));
        const weekDelta = Math.abs(index - fromIndex);

        const scrollFwd = Math.sign(index - fromIndex);

        if (useSmooth && weekDelta > WEEK_ROW_JUMP_USE_FADE_THRESHOLD && fadeWrap) {
          runDirectionalMonthJumpTransition(fadeWrap, outer, targetTop, reduceMotion, scrollFwd === 0 ? 1 : scrollFwd);
          return;
        }

        outer.scrollTo({ top: targetTop, behavior: useSmooth ? 'smooth' : 'auto' });
        return;
      }
      if (listRef.current) {
        listRef.current.scrollToItem(index, 'start');
      }
    },
    [weeks, clearSnapIdleTimer, rowHeightPx]
  );

  useLayoutEffect(() => {
    if (listHeight <= 0) {
      setCalendarGridReady(false);
      initialScrollSyncedRef.current = false;
      return;
    }
    if (!weeks.length) return;

    if (!calendarGridReady) {
      scrollToMonth(currentMonth, { behavior: 'auto' });
      initialScrollSyncedRef.current = true;
      setCalendarGridReady(true);
      userInitiatedScrollToRef.current = false;
      return;
    }

    if (!userInitiatedScrollToRef.current) return;
    userInitiatedScrollToRef.current = false;
    scrollToMonth(currentMonth, { behavior: 'smooth' });
    initialScrollSyncedRef.current = true;
  }, [calendarGridReady, currentMonth, listHeight, scrollToMonth, weeks.length]);

  const handleScroll = useCallback(
    ({ scrollOffset, scrollUpdateWasRequested }) => {
      if (!initialScrollSyncedRef.current && !scrollUpdateWasRequested) {
        return;
      }

      /* Snap on user scroll only; ignore programmatic scrollToItem */
      if (!scrollUpdateWasRequested && weeks.length > 0 && rowHeightPx > 0) {
        clearSnapIdleTimer();
        snapIdleTimerRef.current = window.setTimeout(runWeekSnap, WEEK_SNAP_IDLE_MS);
      }

      if (!weeks.length || rowHeightPx <= 0) return;

      /** During react-window programmatic scroll (`scrollUpdateWasRequested`) and smooth week snap,
       *  derive `currentMonth` from `scrollOffset` anyway — otherwise muted “other month” cells only
       *  refresh after snapping finishes. */
      if (!initialScrollSyncedRef.current) return;

      const topIndex = Math.floor(scrollOffset / rowHeightPx);
      const centerOffset = Math.max(0, Math.floor((rowsTarget - 1) / 2));
      const anchorWeekIndex = Math.min(
        Math.max(0, topIndex + centerOffset),
        weeks.length - 1
      );
      const week = weeks[anchorWeekIndex];
      if (!week || week.length < 4) return;
      const refDate = week[3];
      const year = refDate.getFullYear();
      const month = refDate.getMonth();
      setCurrentMonth((prev) => {
        if (prev.getFullYear() === year && prev.getMonth() === month) return prev;
        return new Date(year, month, 1);
      });
    },
    [weeks, rowHeightPx, rowsTarget, runWeekSnap, clearSnapIdleTimer]
  );
  const setSelectedCalendarIds = useCallback((ids) => {
    setSelectedCalendarIdsState(ids);
    persistMultiCalendarFilter(STORAGE_KEY_CALENDAR_FILTER, ids);
  }, []);

  useEffect(() => {
    const next = coerceMultiCalendarFilter(selectedCalendarIds, calendars);
    if (multiCalendarFiltersEqual(selectedCalendarIds, next)) return;
    setSelectedCalendarIds(next);
  }, [calendars, selectedCalendarIds, setSelectedCalendarIds]);

  const handleGoogleManualSync = useCallback(async () => {
    if (googleSyncBusy) return;
    setGoogleSyncBusy(true);
    try {
      const body =
        selectedCalendarIds !== null &&
        Array.isArray(selectedCalendarIds) &&
        selectedCalendarIds.length === 1
          ? { calendar_id: selectedCalendarIds[0] }
          : {};
      const data = await plannerGoogleManualSync(body);
      const n = Array.isArray(data?.summaries) ? data.summaries.length : 0;
      toast.success(
        n > 0
          ? `Sync finished (${n} calendar${n === 1 ? '' : 's'})`
          : 'Sync finished — no linked calendars synced',
      );
      if (typeof refreshEvents === 'function') await refreshEvents();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        'Google sync failed';
      toast.error(typeof msg === 'string' ? msg : 'Google sync failed');
    } finally {
      setGoogleSyncBusy(false);
    }
  }, [googleSyncBusy, selectedCalendarIds, refreshEvents]);

  const eventsExcludingTodoCalendars = useMemo(() => {
    const allowed = new Set(calendars.map((c) => c.id));
    return events.filter((e) => allowed.has(e.calendar?.id ?? NaN));
  }, [events, calendars]);

  // ##### Filter events by selected calendars (null = all visible non–to-do calendars)
  const filteredEvents = useMemo(() => {
    if (selectedCalendarIds === null) {
      return eventsExcludingTodoCalendars;
    }
    if (selectedCalendarIds.length === 0) {
      return [];
    }
    const allowed = new Set(selectedCalendarIds);
    return eventsExcludingTodoCalendars.filter((e) => allowed.has(e.calendar?.id ?? NaN));
  }, [eventsExcludingTodoCalendars, selectedCalendarIds]);

  const searchedEvents = useMemo(() => {
    const term = eventSearch.trim().toLowerCase();
    if (!term) return filteredEvents;
    return filteredEvents.filter((event) => {
      const title = (event.title || '').toLowerCase();
      const description = (event.description || '').toLowerCase();
      return title.includes(term) || description.includes(term);
    });
  }, [filteredEvents, eventSearch]);

  const eventsByDateKey = useMemo(
    () => buildEventsByDateKeyIndex(searchedEvents),
    [searchedEvents]
  );

  const daySheetEvents = useMemo(() => {
    if (!daySheet?.dateKey) return [];
    return eventsByDateKey.get(daySheet.dateKey) ?? [];
  }, [daySheet, eventsByDateKey]);

  const closeDaySheet = useCallback(() => setDaySheet(null), []);

  const handleDayClick = useCallback((dateKey, date) => {
    setDaySheet({ dateKey, date });
  }, []);

  const handleNavigateDaySheet = useCallback((nextKey, nextDate) => {
    setDaySheet({ dateKey: nextKey, date: nextDate });
  }, []);

  const handleEventClickFromDaySheet = useCallback(
    (event) => {
      closeDaySheet();
      onEventClick?.(event);
    },
    [closeDaySheet, onEventClick],
  );

  const handleNewEventFromDaySheet = useCallback(() => {
    const dateKey = daySheet?.dateKey;
    closeDaySheet();
    if (dateKey) onNewEventForDate?.(dateKey);
  }, [daySheet, closeDaySheet, onNewEventForDate]);

  const handleSelectPickerMonth = useCallback(
    (year, monthIdx) => {
      const y = Number(year);
      const m = Number(monthIdx);
      if (
        Number.isNaN(y) ||
        Number.isNaN(m) ||
        y < minPickerYear ||
        y > maxPickerYear ||
        m < 0 ||
        m > 11
      ) {
        return;
      }
      clearSnapIdleTimer();
      userInitiatedScrollToRef.current = true;
      setCurrentMonth(new Date(y, m, 1));
    },
    [clearSnapIdleTimer, minPickerYear, maxPickerYear],
  );

  const handleToday = () => {
    clearSnapIdleTimer();
    userInitiatedScrollToRef.current = true;
    setCurrentMonth(new Date());
  };
  
  const Row = useCallback(({ index, style }) => {
    const week = weeks[index];
    if (!week) return <div style={style} />;
    return (
      <div
        style={{ ...style, boxSizing: 'border-box', overflow: 'hidden' }}
        className="min-h-0"
      >
        <CalendarWeekRow
          week={week}
          referenceMonth={currentMonth}
          todayKey={todayKey}
          eventsByDateKey={eventsByDateKey}
          onDayClick={handleDayClick}
          onEventClick={onEventClick}
          rowHeightPx={rowHeightPx}
          timeFormatPreference={timeFormatPreference}
          calendarMonthChipMobileUx={isMobile}
        />
      </div>
    );
  }, [
    weeks,
    currentMonth,
    todayKey,
    eventsByDateKey,
    handleDayClick,
    onEventClick,
    rowHeightPx,
    timeFormatPreference,
    isMobile,
  ]);

  if (isEventsLoading) {
    return (
      <div className="flex h-full max-w-full min-w-0 flex-1 flex-col overflow-x-hidden px-1">
        <div className="relative flex min-h-[min(480px,calc(100vh-12rem))] flex-1 overflow-hidden rounded-lg border border-dashed border-slate-200/95">
          <div className={CAL_GRID_WASH_CLASSES} aria-hidden />
          <div className={CAL_GRID_LINES_CLASSES} aria-hidden />
          <div className="relative z-[1] flex flex-1 flex-col items-center justify-center px-2">
            <CalendarViewportLoading title="Loading calendar" subtitle="Fetching your events..." />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex h-full max-w-full min-w-0 flex-col overflow-x-hidden px-1">
      {/* Header with navigation and filter */}
      <div className="shrink-0">
        <CalendarHeader
          currentMonth={currentMonth}
          minPickerYear={minPickerYear}
          maxPickerYear={maxPickerYear}
          onSelectMonth={handleSelectPickerMonth}
          onToday={handleToday}
          calendars={calendars}
          calendarFilterValue={selectedCalendarIds}
          onCalendarFilterChange={setSelectedCalendarIds}
          eventSearch={eventSearch}
          onEventSearchChange={setEventSearch}
          onGoogleManualSync={handleGoogleManualSync}
          googleSyncBusy={googleSyncBusy}
        />
      </div>

      {/* Sticky weekday header + virtualized week rows; same horizontal gutter as header */}
      <div
        ref={scrollContainerRef}
        className="flex min-h-0 flex-1 flex-col min-w-0"
      >
        <div className="sticky top-0 z-10 shrink-0 bg-transparent">
          <CalendarWeekHeader />
        </div>
        {listHeight > 0 && weeks.length > 0 ? (
          <div
            className={`
              relative isolate min-h-0 flex-1 min-w-0 overflow-hidden rounded-br-xl border-t border-l border-slate-200/95 bg-white
              transition-opacity duration-150 ease-out
              ${listSnapPulse ? 'opacity-[0.93]' : 'opacity-100'}
            `}
          >
            <div className={CAL_GRID_WASH_CLASSES} aria-hidden />
            <div className={CAL_GRID_LINES_CLASSES} aria-hidden />

            {!calendarGridReady && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center overflow-hidden"
                aria-busy
                aria-label="Opening calendar grid"
              >
                <div className={CAL_GRID_WASH_CLASSES} aria-hidden />
                <div className={`${CAL_GRID_LINES_CLASSES} opacity-[0.48]`} aria-hidden />
                <div className="relative z-[21] mx-3 rounded-xl border border-slate-200/90 bg-white/96 px-5 py-1 shadow-lg shadow-slate-300/35">
                  <CalendarViewportLoading
                    compact
                    title="Opening calendar"
                    subtitle="Scrolling to your month…"
                  />
                </div>
              </div>
            )}
            <div
              ref={calendarListFadeRef}
              className={`relative z-[1] h-full min-h-0 min-w-0 overflow-hidden ${calendarGridReady ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            >
              <List
                ref={listRef}
                outerRef={listOuterRef}
                outerElementType={CalendarListOuter}
                height={listHeight}
                itemCount={weeks.length}
                itemSize={rowHeightPx}
                width="100%"
                overscanCount={OVERSCAN_COUNT}
                onScroll={handleScroll}
                style={{ overflowX: 'hidden' }}
              >
                {Row}
              </List>
            </div>
          </div>
        ) : (
          <div className="relative isolate flex min-h-[min(320px,calc(100vh-14rem))] min-w-0 flex-1 flex-col items-center justify-center overflow-hidden rounded-b-lg rounded-br-lg border border-t border-l border-slate-200/90 bg-white">
            <div className={CAL_GRID_WASH_CLASSES} aria-hidden />
            <div className={CAL_GRID_LINES_CLASSES} aria-hidden />
            <div className="relative z-[1] flex flex-col items-center justify-center px-2">
              <CalendarViewportLoading
                title="Calendar"
                subtitle="Measuring viewport and preparing rows…"
              />
            </div>
          </div>
        )}
      </div>

      <DayEventsDialog
        open={Boolean(daySheet)}
        onClose={closeDaySheet}
        dateKey={daySheet?.dateKey}
        dayEvents={daySheetEvents}
        onNavigateDay={handleNavigateDaySheet}
        onEventClick={handleEventClickFromDaySheet}
        onNewEvent={handleNewEventFromDaySheet}
        timeFormatPreference={timeFormatPreference}
      />
    </div>
  );
};

export default PlannerCalendarView;
